'use client';

import { useMemo } from 'react';

import { useUser } from 'src/hooks/use-user';
import { usePlanAccess } from 'src/hooks/use-plan-access';

import { readPlanEndDate } from 'src/utils/plan/plan-active';
import { TRIAL_PAY_BONUS_MONTHS } from 'src/utils/plan/plan-period';
import { isPlanTrackAreaLocked } from 'src/utils/plan/plan-track-gate';
import { isBrandAccountUnipileFailed } from 'src/utils/unipile-brand-account';

import { usePaymentBonusUsed } from 'src/sections/common/hooks/load/use-payment-bonus-used';

import { isTrackingSetupBlank } from './service-popup';

import type { ServicePopupFacts } from './use-service-popup';

/**
 * 서비스 팝업의 재료 — 체험 남은 날 · 초기 설정 안 함 · 연동 끊긴 인스타그램 자사 계정.
 * 🔑 구독을 새로 걸지 않는다 — 서비스 틀이 이미 문 구독본(워크스페이스 · 세트)을 받는다. 방금 고친 설정이 화면을 옮기지 않아도 온다.
 *   구독본이 아직 없으면 auth 사본(라우트가 바뀔 때 갱신)으로 센다.
 * @param live 서비스 틀이 문 구독본 — 워크스페이스 문서 · 타겟 키워드 세트 · 세트를 한 번이라도 받았나(「없음」과 「아직」을 가른다)
 * @returns 고르는 재료 · 「다시 보지 않기」가 적을 `onboarding.status` · 워크스페이스 id
 */
export function useServicePopupFacts(live: { workspace: Record<string, any> | null; campaigns: readonly Record<string, any>[]; campaignsReady: boolean }) {
  const { campaigns, campaignsReady } = live;
  const user = useUser();
  const { workspace, config: planConfig, planId } = usePlanAccess();
  const doc = (live.workspace ?? workspace ?? null) as Record<string, any> | null;
  const workspaceId = String(user?.lastWorkspace ?? '').trim();

  const planEnd = readPlanEndDate(doc?.subscriptionPlan)?.getTime() ?? null;
  const trial = useMemo(
    () => (planId === 'plan_trial' && planEnd !== null ? { daysLeft: Math.max(0, Math.ceil((planEnd - Date.now()) / DAY_MS)), endMs: planEnd } : null),
    [planId, planEnd],
  );
  const bonusUsed = usePaymentBonusUsed(trial ? workspaceId : null);

  const facts = useMemo<ServicePopupFacts>(() => {
    const rawAccounts: Record<string, any>[] = Array.isArray(doc?.brandSetting?.brandAccounts) ? doc.brandSetting.brandAccounts : [];
    // 워크스페이스의 모든 사람이 같은 장을 받는다 · `onboarding.status` 칸이 없는 워크스페이스는 접기를 적을 수 없어 대상이 아니다
    const broken = isPlanTrackAreaLocked(planConfig, 'brandAccount')
      ? []
      : rawAccounts
        .filter((row) => row && typeof row === 'object' && String(row.platform ?? '').toLowerCase() === 'instagram' && isBrandAccountUnipileFailed(row))
        .map((row) => String(row.username ?? row.instagramUsername ?? row.id ?? row.handle ?? row.account ?? '').trim())
        .filter(Boolean);
    return {
      /** 재료가 다 왔다 — 워크스페이스 · 타겟 키워드 세트 · (체험이면) 결제 혜택 이력 */
      ready: Boolean(doc) && campaignsReady && (!trial || bonusUsed !== undefined),
      trial,
      bonusMonths: bonusUsed === false ? TRIAL_PAY_BONUS_MONTHS : 0,
      setup: doc?.onboarding?.status === 'pending' && isTrackingSetupBlank(doc, campaigns, planConfig),
      brokenAccounts: broken,
    };
  }, [doc, planConfig, campaigns, campaignsReady, trial, bonusUsed]);

  return { facts, onboardingStatus: doc?.onboarding?.status as unknown, workspaceId };
}

// ---- 이하 비공개 ----------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;
